function initProductCards() {
  class ProductCard extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        slider: '[data-slider]',
        sliderPrev: '[data-slider-prev]',
        sliderNext: '[data-slider-next]',
        link: '[data-link]',
        slideVideo: '[data-slide-video]',
      };

      this.slider = this.querySelector(this.selectors.slider);
      this.sliderPrev = this.querySelector(this.selectors.sliderPrev);
      this.sliderNext = this.querySelector(this.selectors.sliderNext);
      this.sliderVideos = this.slider?.querySelectorAll('video');
      this.productCardLinks = this.querySelectorAll(this.selectors.link);

      this.mediaHoverQuery = window.matchMedia('(hover: hover)');

      this.initLinksListener();

      if (window?.ga4items?.length) {
        this.initDataLayer();
      }

      this.initSlider();

      this.addEventListener('mouseenter', this.handleImagesEagerLoading.bind(this), {once: true});

      this.addEventListener('mouseenter', this.handleSliderVideos.bind(this, 'play'));
      this.addEventListener('mouseleave', this.handleSliderVideos.bind(this, 'pause'));

      this.slider?.addEventListener('touchstart', this.handleImagesEagerLoading.bind(this), {once: true});
    }

    /*
     * Listen to product card clicks and store info in sessionStorage
     * so we can scroll to clicked product when back button is clicked
     */
    initLinksListener() {
      if (!window.theme?.collection?.enableScrollToClickedProduct) return;

      // Only trigger on collection page
      if (document.body.dataset?.template !== 'collection') return;

      const productHandle = this.dataset.productHandle;
      const collection = this.dataset.collection;

      if (!productHandle || !collection) return;

      const dataToStore = {
        collection: collection,
        selectedProduct: productHandle,
      };

      this.productCardLinks.forEach((productCardLink) => {
        productCardLink.addEventListener('click', (event) => {
          sessionStorage.setItem('collection_clicked_product', JSON.stringify(dataToStore));
        });
      });
    }

    initDataLayer() {
      this.productCardLinks.forEach((productCardLink) => {
        productCardLink.addEventListener('click', (event) => {
          const productId = Number(this.dataset.productId);
          const selectedItem = window.ga4items.find((item) => item.item_id === productId);

          if (!selectedItem) return;

          window.dataLayer.push({ecommerce: null});
          window.dataLayer.push({
            event: 'custom.ecommerce.select_item',
            action: 'select_item',
            ecommerce: {
              items: [selectedItem],
            },
          });
        });
      });
    }

    initSlider() {
      if (!this.slider) return;

      this.sliderPrev?.addEventListener('click', () => {
        this.changeSlide('prev');
      });

      this.sliderNext?.addEventListener('click', () => {
        this.changeSlide('next');
      });

      // Start with 2nd slide on hover devices
      if (this.mediaHoverQuery.matches) {
        this.changeSlide('next');
      }

      document.addEventListener(
        'results-grid:loaded',
        () => {
          if (this.mediaHoverQuery.matches) {
            this.changeSlide('next');
          }
        },
        {once: true}
      );

      this.mediaHoverQuery.addEventListener('change', () => {
        if (this.mediaHoverQuery.matches) {
          this.changeSlide('next');
          return;
        }

        // reset slider
        this.slider.scrollLeft = 0;
        this.sliderPrev.disabled = true;
      });

      if (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        window.innerWidth < 1025
      ) {
        // Select the video element
        const videos = document.querySelectorAll(this.selectors.slideVideo);

        const callback = (entries, observer) => {
          entries.forEach((entry) => {
            const video = entry.target;

            if (entry.isIntersecting) {
              // Play the video only if it's not already playing
              if (video.paused) {
                video.play();
              }
            } else {
              // Pause the video
              if (!video.paused) {
                video.pause();
              }
            }
          });
        };

        // Create an Intersection Observer
        const observer = new IntersectionObserver(callback, {});

        // Start observing each video element
        videos.forEach((video) => {
          observer.observe(video);
        });
      }
    }

    handleSliderVideos(state) {
      if (!this.mediaHoverQuery.matches) return;
      if (!this.sliderVideos?.length) return;

      this.sliderVideos.forEach((video) => {
        if (state === 'play' && video.paused) {
          video.play();
        } else {
          video.pause();
        }
      });
    }

    changeSlide(direction) {
      const {scrollLeft, clientWidth, scrollWidth} = this.slider;
      const scrollPos = direction === 'prev' ? scrollLeft - clientWidth : scrollLeft + clientWidth;

      this.slider.scroll({
        left: scrollPos,
        behavior: 'smooth',
      });

      this.sliderPrev.disabled = scrollPos === 0;
      this.sliderNext.disabled = scrollPos + clientWidth >= scrollWidth;
    }

    handleImagesEagerLoading() {
      const images = this.slider?.querySelectorAll('img');
      images?.forEach((image) => {
        image.loading = 'eager';
      });
    }
  }

  customElements.define('product-card', ProductCard);
}

export default initProductCards;
